module Client.Reauthenticate

open Client
open Elmish
open Shared
open Illy.SafeStack.Client
open System
open Illy.SafeStack
open Fable.React 
open Shared.LogIn
open Fable.React.Props

type MFA =
    {
        Code: Text.Model
        ReSent: bool
        Mode: Shared.LogIn.MFAMode
    }

type Model = 
    {
        Username: string
        Password: Text.Model
        LoginError: string option
        Saving: bool
        ExpectingMFA: MFA option
        IsIlly: bool
        IsSSO: bool
    }

let toCredentials (model: Model) = 
    {
        Username = model.Username
        Password = Text.toString model.Password |> Option.defaultValue ""
    }

module Validations = 
    open Illy.SafeStack.Validation
    open Shared.LogIn.Validations

    let resetValidations (model: Model) = 
        {model with 
            Password = Text.resetValidations model.Password
            LoginError = None 
        }
        
    let dispatchValidations model = 
        let model = resetValidations model 
        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            let errors = 
                err 
                |> ValidationInfrastructure.bundleValidations 
                    (fun x -> 
                        (match x with 
                        | LogInValidations.Username _ -> 0 
                        | LogInValidations.Password _ -> 0
                        | LogInValidations.GeneralError _ -> 1
                        | LogInValidations.Timeout _ -> 1), LogInValidations.toString x)
                    2
            {model with 
                Password = Text.setValidations model.Password errors.[0] None
                LoginError = errors.[1]
            }

type Msg = 
    | SaveResult of LoginResult
    | PasswordMsg of Text.Msg
    | MFAMsg of Text.Msg
    | CloseOverlay  
    | SaveClicked 
    | MFAResponse of MFAResult
    | MFAHalfLoggedIn
    | LogOutOnServer
    | LoggedOutOnServer of unit
    | LoggedOut
    | ApiError of ApiError

type Event = 
    | CloseOverlay 
    | SaveSuccessful of updatedUser: ClientSideUser * notification: string
    | ApiError of ApiError

module Initialisations = 
    let initPassword value =
        { Text.CustomSettings.defaultOptions() with TextType = Text.TextType.Password }
        |> Text.init "Password" "Password" (Some "Enter Password") 250. true value

module Calls =
    let login model= Api.unsecureCallSuccess<ILoginApi,_,_,_> (fun api -> api.login) (toCredentials model |> Entered) SaveResult
    let logout username = Api.secureCall SecuredRoute.Standard (fun api -> api.logout) username LoggedOutOnServer Msg.ApiError

let init isIlly isSSO username =
    {
        Username = username
        Password = Initialisations.initPassword None
        LoginError = None
        Saving = false
        ExpectingMFA = None
        IsIlly = isIlly
        IsSSO = isSSO
    }, []

let update msg model = 
    match msg with
    | PasswordMsg cMsg ->
        let m, c = Text.update cMsg model.Password
        { model with Password = m }, Cmd.map PasswordMsg c |> SideEffect.Cmd
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event
    | Msg.ApiError err ->
        { model with Saving = false }, ApiError err |> SideEffect.Event
    | SaveClicked -> 
        match model.ExpectingMFA with
        | None ->
            let vResult =
                LogIn.Validations.validateCredentials
                    (Some model.Username)
                    (Text.toString model.Password)

            if Validation.isSuccessful false vResult then 
                {model with Saving = true},
                Calls.login model |> SideEffect.Cmd
            else 
                Validations.dispatchValidations model vResult, SideEffect.none
        | Some mfa ->
            let code = mfa.Code |> Text.toString |> Option.defaultValue ""
            {
                model with
                    ExpectingMFA = Some {
                        mfa with
                            ReSent = false
                    }
            }, Api.mfaCall<IMfaApi,_,_,_> (fun x -> x.submit) code MFAResponse Msg.ApiError |> SideEffect.Cmd
    | MFAResponse response ->
        match model.ExpectingMFA with
        | None -> model, SideEffect.none
        | Some mfa ->
            match response with
            | Shared.Types.MFAResult.Expired ->
                {
                    model with
                        ExpectingMFA = 
                            Some {
                                mfa with
                                    Code = Text.resetText mfa.Code
                                    ReSent = true
                            }
                }, SideEffect.none
            | Shared.Types.MFAResult.Incorrect ->
                {
                    model with
                        LoginError = Some "The code you have entered is incorrect."
                        ExpectingMFA = 
                            Some {
                                mfa with
                                    Code = Text.resetText mfa.Code
                                    ReSent = true
                            }
                }, SideEffect.none
            | Shared.Types.MFAResult.Success newUser ->
                model, LoginResult.Successful newUser |> SaveResult |> Cmd.ofMsg |> SideEffect.Cmd
            | Shared.Types.MFAResult.Throttle ->
                {
                    model with
                        LoginError = Some "You need to wait before logging in again."
                        ExpectingMFA = 
                            Some {
                                mfa with
                                    Code = Text.resetText mfa.Code
                                    ReSent = true
                            }
                }, SideEffect.none
    | SaveResult result -> 
        match result with 
        | LoginResult.Successful u ->
            model, (u, "You logged in successfully.") |> Event.SaveSuccessful |> SideEffect.Event
        | LoginResult.LoginFailure v ->
            Validations.dispatchValidations {model with Saving = false} v, SideEffect.none
        | LoginResult.MFARequired (mode, u) ->
            let cmd =
                Cmd.OfAsync.perform
                    (Storage.store Storage.USER_STORAGE_KEY) 
                    u 
                    (fun _ -> MFAHalfLoggedIn)
            {
                model with
                    Saving = false
                    ExpectingMFA = Some {
                        Code = Text.init "code" "Verification Code" None 6. true None (Text.CustomSettings.defaultOptions())
                        ReSent = false
                        Mode = mode
                    }
            }, cmd |> SideEffect.Cmd
        | LoginResult.PasswordExpired ->
            { model with LoginError = Some "Your account password has expired. Please log out and back in." }, SideEffect.none
    | MFAHalfLoggedIn ->
        // Exists as a callback for saving a new user token that does not have MFA claims
        model, SideEffect.none
    | MFAMsg cMsg ->
        match model.ExpectingMFA with
        | None -> model, SideEffect.none
        | Some mfa ->
            let m, c = Text.update cMsg mfa.Code
            { model with 
                ExpectingMFA = 
                    Some {
                        mfa with
                            Code = m
                    } 
            }, Cmd.map MFAMsg c |> SideEffect.Cmd
    | LoggedOutOnServer _ ->
        let cmd =
            Cmd.OfAsync.perform
                (fun _ -> Storage.remove Storage.USER_STORAGE_KEY) 
                () 
                (fun _ -> LoggedOut)
        
        model, cmd |> SideEffect.Cmd
    | LogOutOnServer ->
        let user = Storage.get<ClientSideUser> Storage.USER_STORAGE_KEY
        model, Api.secureCall SecuredRoute.Standard (fun x -> x.logout) user.Username LoggedOutOnServer Msg.ApiError |> SideEffect.Cmd
    | LoggedOut ->
        model, Routing.PageRoute.Login |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl |> SideEffect.Cmd

let renderSSO model dispatch =
    [
        p [] [
            str "LINKS Manager is using an external service to log-in. Please log out and back in. "
        ]
        UI.button "Log Out" LogOutOnServer dispatch
    ]

let renderStandard model dispatch =
    [
        match model.ExpectingMFA with
        | None ->
            yield! [
                p [] [
                    str "Your session has expired. Please re-enter your password to continue."
                ]
                p [] [
                    str "Your username is: "
                    str model.Username
                ]

                Text.text false model.Password (PasswordMsg >> dispatch)
            ]
        | Some mfa ->
            match mfa.Mode with
            | MFAMode.Email _ ->
                if mfa.ReSent then
                    yield div [] [ str "Your code has expired. A new verification code has been re-generated and sent to you." ]
            
                yield p [] [
                    str "Please check your inbox for the verification code. If you haven't received this email please refresh your inbox and check your spam folder."
                ]
            | MFAMode.App (Some _) ->
                yield p [] [
                    str "Cannot re-authenticate because you are using App-based MFA but it is not fully set-up. Please log out and back in."
                ]
                yield UI.button "Log Out" LogOutOnServer dispatch
            | MFAMode.App None ->
                yield p [] [
                    str "Please obtain a login code from your device."
                ]

            yield Text.text false mfa.Code (MFAMsg >> dispatch)
    ]

let view model dispatch = 
    jQueryUI.modal "Your session has expired" 10000 Msg.CloseOverlay dispatch (Some 600) [
        form [
            OnSubmit 
                (fun ev -> 
                    ev.preventDefault()
                    (dispatch Msg.SaveClicked)
                )
        ] [
            if model.IsIlly then
                yield div [ ClassName "illy-info" ] [ str "All Illy users will be allowed to log-in regardless of SSO." ]

            if model.IsIlly || model.IsSSO = false then
                yield! renderStandard model dispatch
            else
                yield! renderSSO model dispatch

            yield UI.renderWarning (Some "Note: the page will be reloaded afterwards.")
            yield UI.renderError model.LoginError
            yield! UI.saveControls (Some "Log In") false model.Saving SaveClicked Msg.CloseOverlay dispatch
        ]
    ]